import { CheckOutlined, CloseCircleFilled, EditOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { debtApi } from "api/debt.api";
import { AppTable, AppTableToolbar } from "components/antd-ui";
import { ActionButton, getTableShowingRange, TableContent, TableLayout, TableLayoutFooter, TableTotals } from "components/table";
import { fetchCustomerOptions, useRemoteOptions } from "hooks/useRemoteOptions";
import { useSearchInput } from "hooks/useSearchInput";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Debt, DebtTotals } from "types/debt";
import { getTitle } from "utils";
import { can } from "utils/auth";
import dayjs from "utils/dayjs";
import { formatMoney } from "views/Opportunity/opportunity.meta";
import { DebtUpdateModal } from "./components/DebtUpdateModal";

const debtPath = "/debt";

const displayDate = (value?: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "--");

export const DebtPage = observer(({ title = "" }) => {
  const searchInput = useSearchInput();
  const search = searchInput.debounced;
  const [customerId, setCustomerId] = useState<number>();
  const [paging, setPaging] = useState({ page: 1, limit: 50 });
  const [rows, setRows] = useState<Debt[]>([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState<DebtTotals>({ amount: 0, count: 0 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<{ debt: Debt; mode: "collect" | "due" } | null>(null);

  const canUpdate = can(debtPath, "UPDATE");
  const customerFilter = useRemoteOptions(fetchCustomerOptions);
  const today = dayjs();

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await debtApi.findAll({ page: paging.page, limit: paging.limit, search, customerId });
      setRows(res.data.debts || []);
      setTotal(res.data.total || 0);
      setTotals(res.data.totals || { amount: 0, count: 0 });
    } finally {
      setLoading(false);
    }
  }, [paging.page, paging.limit, search, customerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetPage = useCallback(() => setPaging((prev) => ({ ...prev, page: 1 })), []);

  const columns = useMemo<TableColumnsType<Debt>>(
    () => [
      {
        title: "Khách hàng",
        key: "customer",
        render: (_, row) => (
          <div>
            <div className="font-semibold">{row.customer?.name || "--"}</div>
            <div className="text-xs text-gray-500">{row.staff ? `Sale: ${row.staff.name}` : "Chưa gán sale"}</div>
          </div>
        ),
      },
      {
        title: "Đơn hàng",
        key: "order",
        width: 190,
        render: (_, row) => (
          <div>
            <div className="font-semibold">{row.order?.code || "--"}</div>
            <div className="text-xs text-gray-500">{row.order?.contractCode ? `HĐ: ${row.order.contractCode}` : "--"}</div>
          </div>
        ),
      },
      {
        title: "Mã báo giá",
        key: "quote",
        width: 200,
        render: (_, row) => (
          <div>
            <TableContent>{row.opportunity?.code || "--"}</TableContent>
            <div className="text-xs text-gray-500">{row.opportunity?.name || ""}</div>
          </div>
        ),
      },
      { title: "Đợt thu", dataIndex: "content", key: "content", render: (value: string) => <TableContent>{value || "--"}</TableContent> },
      {
        title: "Hạn thu",
        dataIndex: "dueDate",
        key: "dueDate",
        width: 130,
        render: (value: string) => {
          const overdue = !!value && dayjs(value).isBefore(today, "day");
          return overdue ? (
            <div>
              <span className="font-semibold text-red-600">{displayDate(value)}</span>
              <div className="text-xs text-red-500">Quá hạn</div>
            </div>
          ) : (
            <TableContent>{displayDate(value)}</TableContent>
          );
        },
      },
      {
        title: "Số tiền (gồm VAT)",
        dataIndex: "amount",
        key: "amount",
        width: 160,
        align: "right",
        render: (value: number) => <span className="font-semibold">{formatMoney(value)}đ</span>,
      },
      ...(canUpdate
        ? [
            {
              title: "Thao tác",
              key: "action",
              width: 190,
              align: "center" as const,
              render: (_: unknown, row: Debt) => (
                <div className="flex gap-2 justify-center items-center">
                  <ActionButton className="tableActionButton--success" icon={<CheckOutlined />} onClick={() => setEditing({ debt: row, mode: "collect" })}>
                    Đã thu
                  </ActionButton>
                  <Tooltip title="Sửa hạn thu">
                    <ActionButton aria-label="Sửa hạn thu" onClick={() => setEditing({ debt: row, mode: "due" })}>
                      <EditOutlined style={{ color: "#6c84e5" }} />
                    </ActionButton>
                  </Tooltip>
                </div>
              ),
            },
          ]
        : []),
    ],
    [canUpdate, today],
  );

  const { showingFrom, showingTo } = getTableShowingRange(paging.page, paging.limit, total);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: searchInput.value,
              width: 380,
              placeholder: "Tìm theo mã đơn, mã báo giá, mã hợp đồng, khách hàng",
              onChange: (value) => {
                resetPage();
                searchInput.onChange(value);
              },
              inputProps: { allowClear: { clearIcon: <CloseCircleFilled onClick={searchInput.clear} /> } },
            }}
            filterLabel={null}
            filters={[
              {
                key: "customerId",
                value: customerId ?? "",
                options: customerFilter.options,
                onChange: (value: string | number | undefined) => {
                  const next = typeof value === "number" ? value : undefined;
                  customerFilter.select(next);
                  resetPage();
                  setCustomerId(next);
                },
                placeholder: "Khách hàng",
                allowClear: true,
                showSearch: true,
                filterOption: false as const,
                onSearch: customerFilter.onSearch,
                width: 240,
              },
            ]}
          />
        }
        tabs={
          <TableTotals
            items={[
              { label: "Tổng số tiền (gồm VAT)", value: `${formatMoney(totals.amount)}đ`, tone: "primary" },
              { label: "Tổng số dòng", value: formatMoney(totals.count) },
            ]}
            note="Các đợt thu chưa thu của đơn hàng (không gồm đơn thất bại)."
          />
        }
        table={<AppTable<Debt> rowKey="id" loading={loading} pagination={false} dataSource={rows} columns={columns} />}
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={paging.page}
            pageSize={paging.limit}
            onChange={setPaging}
          />
        }
      />

      <DebtUpdateModal
        debt={editing?.debt || null}
        mode={editing?.mode || "collect"}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          fetchData();
        }}
      />
    </div>
  );
});
